/**
 * MIDI bytes → decoded messages. One parser per input port; it holds that port's running status, 14-bit pairs and NRPN selection.
 *
 * Emits (channel 1–16):
 *   { type: 'note',      channel, number, value: vel/127, on }       note-on velocity 0 is an off
 *   { type: 'cc',        channel, number, raw, value: raw/127 }
 *   { type: 'cc14',      channel, number, raw, value: raw/16383 }    `number` is the MSB controller, 0–31
 *   { type: 'nrpn',      channel, number, raw, value }               `number` is the 14-bit parameter
 *   { type: 'nrpn',      channel, number, delta }                    data increment (+1) / decrement (−1)
 *   { type: 'pitchbend', channel, number: 0, raw, value: raw/16383 }
 *   { type: 'pc',        channel, number }
 *
 * ⚠️ PAIRS ARE COLLECTED, NOT ORDERED. A 14-bit CC (n + n+32) or an NRPN data entry (6 + 38) emits once both halves arrived within `pairWindowMs`, in either order, or when the window closes with one. A lone LSB reuses the last MSB (a fine move), a lone MSB has LSB 0. WEBMIDI.js has no 14-bit pairing and drops NRPN on out-of-order CCs — the reason this exists.
 *
 * An NRPN whose LSB never arrived is 7-bit (`raw` 0–127); one that has sent an LSB once is 14-bit from then on.
 *
 * Which CCs pair is the caller's call (`is14bit`): a controller sending 7-bit on CC 1 and an unrelated knob on CC 33 is common, so pairing is only by binding. RPNs (101/100) are consumed and ignored; clock, active sensing, sysex and the rest of the system messages are dropped.
 */

const DATA_LENGTH = { 0x8: 2, 0x9: 2, 0xa: 2, 0xb: 2, 0xc: 1, 0xd: 1, 0xe: 2 }
const SYSTEM_LENGTH = { 0xf1: 1, 0xf2: 2, 0xf3: 1 }

export const createParser = ({
	onMessage,
	is14bit = () => false,
	pairWindowMs = 10,
	setTimer = setTimeout,
	clearTimer = clearTimeout,
} = {}) => {
	let status = 0
	let data = []
	let sysex = false
	// Pending halves, keyed `cc:<ch>:<msb cc>` / `nrpn:<ch>:<param>`.
	const pairs = new Map()
	const lastMsb = new Map()
	// Per channel: { mode: 'nrpn'|'rpn'|null, msb, lsb }.
	const selection = new Map()
	const fine = new Set()

	const emit = m => onMessage?.(m)

	const flushPair = key => {
		const p = pairs.get(key)
		if (!p) return
		pairs.delete(key)
		if (p.timer !== undefined) clearTimer(p.timer)
		const msb = p.msb ?? lastMsb.get(key) ?? 0
		lastMsb.set(key, msb)
		if (p.kind === 'cc14') {
			const raw = (msb << 7) | (p.lsb ?? 0)
			emit({ type: 'cc14', channel: p.channel, number: p.number, raw, value: raw / 16383 })
			return
		}
		if (p.lsb !== undefined) fine.add(key)
		if (fine.has(key)) {
			const raw = (msb << 7) | (p.lsb ?? 0)
			emit({ type: 'nrpn', channel: p.channel, number: p.number, raw, value: raw / 16383 })
		} else emit({ type: 'nrpn', channel: p.channel, number: p.number, raw: msb, value: msb / 127 })
	}

	const half = (key, init, part, v) => {
		let p = pairs.get(key)
		if (!p) {
			p = { ...init }
			pairs.set(key, p)
		}
		p[part] = v
		if (p.msb !== undefined && p.lsb !== undefined) return flushPair(key)
		if (p.timer === undefined) p.timer = setTimer(() => flushPair(key), pairWindowMs)
	}

	const controlChange = (channel, cc, v) => {
		const sel = selection.get(channel) ?? { mode: null, msb: 0, lsb: 0 }
		// NRPN / RPN parameter select.
		if (cc === 99 || cc === 98) {
			sel.mode = 'nrpn'
			sel[cc === 99 ? 'msb' : 'lsb'] = v
			selection.set(channel, sel)
			return
		}
		if (cc === 101 || cc === 100) {
			sel[cc === 101 ? 'msb' : 'lsb'] = v
			// 127/127 is the RPN null: data entry goes back to being plain CCs.
			sel.mode = sel.msb === 127 && sel.lsb === 127 ? null : 'rpn'
			selection.set(channel, sel)
			return
		}
		if (sel.mode && (cc === 6 || cc === 38 || cc === 96 || cc === 97)) {
			if (sel.mode === 'rpn') return
			const number = (sel.msb << 7) | sel.lsb
			if (cc === 96 || cc === 97) {
				emit({ type: 'nrpn', channel, number, delta: cc === 96 ? 1 : -1 })
				return
			}
			half(`nrpn:${channel}:${number}`, { kind: 'nrpn', channel, number }, cc === 6 ? 'msb' : 'lsb', v)
			return
		}
		if (cc < 32 && is14bit(channel, cc)) {
			half(`cc:${channel}:${cc}`, { kind: 'cc14', channel, number: cc }, 'msb', v)
			return
		}
		if (cc >= 32 && cc < 64 && is14bit(channel, cc - 32)) {
			half(`cc:${channel}:${cc - 32}`, { kind: 'cc14', channel, number: cc - 32 }, 'lsb', v)
			return
		}
		emit({ type: 'cc', channel, number: cc, raw: v, value: v / 127 })
	}

	const message = (st, d) => {
		const channel = (st & 0x0f) + 1
		switch (st >> 4) {
			case 0x8:
				return emit({ type: 'note', channel, number: d[0], value: 0, on: false })
			case 0x9:
				return emit({ type: 'note', channel, number: d[0], value: d[1] / 127, on: d[1] > 0 })
			case 0xb:
				return controlChange(channel, d[0], d[1])
			case 0xc:
				return emit({ type: 'pc', channel, number: d[0] })
			case 0xe: {
				const raw = d[0] | (d[1] << 7)
				return emit({ type: 'pitchbend', channel, number: 0, raw, value: raw / 16383 })
			}
			// Aftertouch (0xA, 0xD): parsed for running status, not mapped yet.
		}
	}

	let skip = 0

	const push = bytes => {
		for (const b of bytes) {
			if (b >= 0xf8) continue // realtime: clock, active sensing… may sit anywhere, never touch running status
			if (b === 0xf0) {
				sysex = true
				continue
			}
			if (sysex) {
				if (b === 0xf7) {
					sysex = false
					continue
				}
				if (!(b & 0x80)) continue
				// Any other status byte ends a truncated sysex and is handled as itself.
				sysex = false
			}
			if (b & 0x80) {
				data = []
				if (b >= 0xf0) {
					// System common: no running status after it.
					status = 0
					skip = SYSTEM_LENGTH[b] ?? 0
					continue
				}
				status = b
				skip = 0
				continue
			}
			if (skip > 0) {
				skip--
				continue
			}
			if (!status) continue
			data.push(b)
			if (data.length === DATA_LENGTH[status >> 4]) {
				message(status, data)
				data = []
			}
		}
	}

	/** Emit whatever half-pairs are waiting, now. */
	const flush = () => {
		for (const key of [...pairs.keys()]) flushPair(key)
	}

	const reset = () => {
		for (const p of pairs.values()) if (p.timer !== undefined) clearTimer(p.timer)
		pairs.clear()
		selection.clear()
		status = 0
		data = []
		sysex = false
	}

	return { push, flush, reset }
}
